import { Box, Text } from 'folds';
import { Avatar, AvatarShape } from '$components/avatar';
import { userFallbackIcon } from '$components/icons/phosphor';
import type { MouseEventHandler } from 'react';
import { useAtomValue } from 'jotai';
import type { Room, CallMembership } from '$types/matrix-sdk';
import { NavButton, NavItem, NavItemContent } from '$components/nav';
import { UserAvatar } from '$components/user-avatar';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { getMxIdLocalPart } from '$utils/matrix';
import { getAvatarUrl, getMemberAvatarMxc, getMemberDisplayName } from '$utils/room/display';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { useOpenUserRoomProfile } from '$state/hooks/userRoomProfile';
import { useSpaceOptionally } from '$hooks/useSpace';
import { nicknamesAtom } from '$state/nicknames';
import { useCallEmbed } from '$hooks/useCallEmbed';

type RoomNavUserProps = {
  room: Room;
  callMembership: CallMembership;
  hideText?: boolean;
};

export function RoomNavUser({ room, callMembership, hideText }: RoomNavUserProps) {
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const openProfile = useOpenUserRoomProfile();
  const space = useSpaceOptionally();

  const callEmbed = useCallEmbed();
  const isActiveCall = callEmbed?.roomId === room.roomId;

  const userId = callMembership.sender ?? '';
  const avatarMxcUrl = getMemberAvatarMxc(room, userId);
  const avatarUrl = getAvatarUrl(mx, avatarMxcUrl, 32, useAuthentication);
  const nicknames = useAtomValue(nicknamesAtom);
  const name = getMemberDisplayName(room, userId, nicknames) ?? getMxIdLocalPart(userId);
  const isCallParticipant = isActiveCall && userId !== mx.getUserId();

  const handleNavUserClick: MouseEventHandler<HTMLButtonElement> = (evt) => {
    openProfile(
      room.roomId,
      space?.roomId,
      userId,
      undefined,
      evt.currentTarget.getBoundingClientRect()
    );
  };

  const ariaLabel = isCallParticipant ? `Call Participant: ${name}` : name;

  return (
    <NavItem variant="Background" radii="400">
      <NavButton onClick={handleNavUserClick} aria-label={ariaLabel}>
        <NavItemContent as="div" style={hideText ? { padding: '0' } : {}}>
          <Box direction="Column" grow="Yes" gap="200" justifyContent="Stretch">
            <Box alignItems="Center" gap="200" justifyContent={hideText ? 'Center' : 'Start'}>
              <Avatar size="200" shape={AvatarShape.Circle}>
                <UserAvatar
                  userId={userId}
                  src={avatarUrl ?? undefined}
                  alt={name}
                  renderFallback={() => userFallbackIcon('sm')}
                />
              </Avatar>
              {!hideText && (
                <Text as="span" size="B400" priority="300" truncate>
                  {name}
                </Text>
              )}
            </Box>
          </Box>
        </NavItemContent>
      </NavButton>
    </NavItem>
  );
}
